// useMediaPermissions.ts
// Vue composable for media permissions management (shared state — one listener, no mount-time getUserMedia)

import { ref, computed } from 'vue'
import {
  mediaPermissionManager,
  type MediaPermissionState,
  type MediaStreamInfo,
  addPermissionListener,
  checkDeviceAvailability,
  stopMediaStream,
} from '../utils/mediaPermissions'

const permissionState = ref<MediaPermissionState>(
  mediaPermissionManager.getPermissionState()
)
const isLoading = ref(false)
const error = ref<string | null>(null)
const testStream = ref<MediaStream | null>(null)
const deviceAvailability = ref<{ hasCamera: boolean; hasMicrophone: boolean } | null>(
  mediaPermissionManager.getPermissionState().deviceAvailability ?? null
)

let moduleListenersReady = false

function syncFromManager(): void {
  const state = mediaPermissionManager.getPermissionState()
  permissionState.value = state
  if (state.deviceAvailability) {
    deviceAvailability.value = state.deviceAvailability
  }
}

function ensureModuleListeners(): void {
  if (moduleListenersReady) return
  moduleListenersReady = true
  addPermissionListener(syncFromManager)
}

export function useMediaPermissions() {
  ensureModuleListeners()

  const hasPermissions = computed(() => permissionState.value.hasPermissions)
  const needsPermission = computed(() => !permissionState.value.hasPermissions)

  const canMakeVideoCall = computed(
    () => hasPermissions.value && mediaPermissionManager.isSecureContext()
  )

  const browserInfo = computed(() => mediaPermissionManager.getBrowserInfo())

  const checkPermissions = async (quietMode: boolean = false): Promise<MediaPermissionState> => {
    try {
      isLoading.value = true
      error.value = null

      const state = await mediaPermissionManager.checkPermissions(quietMode)
      permissionState.value = state

      if (state.deviceAvailability) {
        deviceAvailability.value = state.deviceAvailability
      }

      return state
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      error.value = message
      console.error('Error checking permissions:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const requestPermissions = async (): Promise<MediaStreamInfo> => {
    try {
      isLoading.value = true
      error.value = null

      const result = await mediaPermissionManager.requestPermissions()

      permissionState.value = mediaPermissionManager.getPermissionState()

      if (result.stream) {
        testStream.value = result.stream
        stopMediaStream(result.stream)
        testStream.value = null
      }

      return result
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      error.value = message
      console.error('Error requesting permissions:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const testDevices = async (): Promise<MediaStreamInfo> => {
    try {
      isLoading.value = true
      error.value = null

      const result = await mediaPermissionManager.testMediaDevices()

      permissionState.value = {
        camera: result.video ? 'granted' : 'denied',
        microphone: result.audio ? 'granted' : 'denied',
        hasPermissions: result.video && result.audio,
      }

      return result
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      error.value = message
      console.error('Error testing devices:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const getErrorMessage = (): string => {
    if (error.value) {
      return error.value
    }
    return mediaPermissionManager.getErrorMessage()
  }

  const cleanup = () => {
    if (testStream.value) {
      testStream.value.getTracks().forEach((track) => track.stop())
      testStream.value = null
    }
  }

  const reset = () => {
    cleanup()
    permissionState.value = {
      camera: 'unknown',
      microphone: 'unknown',
      hasPermissions: false,
    }
    error.value = null
    isLoading.value = false
    mediaPermissionManager.reset()
  }

  const checkDevices = async (): Promise<{ hasCamera: boolean; hasMicrophone: boolean }> => {
    try {
      const availability = await checkDeviceAvailability()
      deviceAvailability.value = availability
      return availability
    } catch (err: unknown) {
      console.error('Error checking device availability:', err)
      throw err
    }
  }

  const requestPermissionsWithRetry = async (): Promise<MediaStreamInfo> => {
    try {
      isLoading.value = true
      error.value = null

      const result = await mediaPermissionManager.requestPermissionsWithRetry()
      permissionState.value = mediaPermissionManager.getPermissionState()

      if (result.stream) {
        stopMediaStream(result.stream)
      }

      return result
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      error.value = message
      console.error('Error requesting permissions with retry:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  return {
    permissionState: computed(() => permissionState.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    testStream: computed(() => testStream.value),
    deviceAvailability: computed(() => deviceAvailability.value),

    hasPermissions,
    needsPermission,
    canMakeVideoCall,
    browserInfo,

    checkPermissions,
    requestPermissions,
    requestPermissionsWithRetry,
    testDevices,
    checkDevices,
    getErrorMessage,
    cleanup,
    reset,
  }
}
